/**
 * Payment-method gating helpers.
 *
 * Two rules live here, both keyed off the same two facts (does the account have
 * a card on file, and is it a Lifetime plan):
 *
 *   1. The "Unlock your Projector" dialogs (app.jsx) must stay out of the
 *      onboarding lanes — the quick-user flow already ends on
 *      /quick-user-payment, which asks for the very same card.
 *   2. A signed-in user with NO payment method is parked on
 *      /quick-user-payment and can't navigate anywhere else until a card is
 *      added. Lifetime users are exempt — they've paid once, forever, so
 *      there's nothing left to bill.
 */

// The backend models "forever" as a 100-year billing interval.
export const LIFETIME_INTERVAL_DAYS = '36500';

// Ten years out — the same window the billing page uses to decide a plan has no
// meaningful renewal date (`isMoreThan365Days` in settings/billing).
const LIFETIME_END_DATE_MS = 3650 * 24 * 60 * 60 * 1000;

const looksLifetime = (value) => typeof value === 'string' && /life\s*-?\s*time/i.test(value);

/**
 * True when the account is on a Lifetime plan.
 *
 * getMySubscription doesn't expose one canonical "is lifetime" flag, so this
 * checks every marker the app already relies on elsewhere:
 *   • the plan NAME ("Lifetime") — what settings/billing compares against
 *   • the plan's interval_in_days === '36500' — what the checkout layout uses
 *   • the matching entry in allPlans, when the subscription payload is thin
 *   • an end_date more than ten years away
 *
 * @param {object} subscriptionDetails getMySubscription response (subDetails)
 * @param {Array}  allPlans            plans list from AppContext (optional)
 */
export const isLifetimePlan = (subscriptionDetails, allPlans) => {
  if (!subscriptionDetails) return false;
  const sub = subscriptionDetails.subscription;

  const names = [
    subscriptionDetails.current_plan,
    sub?.plan,
    sub?.product_name,
    sub?.plan_type,
    sub?.price_description,
  ];
  if (names.some(looksLifetime)) return true;

  if (`${sub?.interval_in_days}` === LIFETIME_INTERVAL_DAYS) return true;

  const planId = sub?.plan_id ?? sub?.id ?? sub?.purchase_plan_id;
  if (planId != null && Array.isArray(allPlans)) {
    const matched = allPlans.find((p) => `${p?.id}` === `${planId}`);
    if (matched) {
      if (`${matched.interval_in_days}` === LIFETIME_INTERVAL_DAYS) return true;
      if ([matched.price_description, matched.plan, matched.product_name].some(looksLifetime)) return true;
    }
  }

  const endDate = subscriptionDetails.end_date || sub?.end_date;
  if (endDate) {
    const parsed = new Date(endDate);
    if (!Number.isNaN(parsed.getTime()) && parsed.getTime() - Date.now() > LIFETIME_END_DATE_MS) {
      return true;
    }
  }

  return false;
};

/**
 * True only when the subscription payload has actually loaded AND reports an
 * empty card list. `undefined` paymentMethods (still loading, or the getMy-
 * Subscription error fallback) deliberately reads as "unknown", never as
 * "no card" — otherwise the gate would fire during the first render.
 */
export const hasNoPaymentMethod = (subscriptionDetails) => Array.isArray(subscriptionDetails?.paymentMethods)
  && subscriptionDetails.paymentMethods.length === 0;

/**
 * ── Whose subscription is this? ──
 *
 * getMySubscription answers for whatever token was in localStorage when the
 * request went out, and AppContext keeps that answer until the next one
 * arrives. Every account switch therefore opens a window where the state
 * describes the PREVIOUS account:
 *
 *   Manager exits an impersonated user (sidebar `managerReliving`) →
 *   isAccessingUser is dropped and handleManger clears mangerToken /
 *   userAccessedData FIRST, then three awaits later getSubscription() finally
 *   returns the manager's own record. In between, "not impersonating" is true
 *   while subscriptionDetails is still the accessed user's paymentMethods: [] —
 *   so the gate parked the MANAGER on the payment page, and the card dialog
 *   (which never resets itself once shown) fired at them too.
 *
 * Stamping each payload with the token that fetched it makes that window
 * detectable: no stamp, or a stamp for another token, means "this record isn't
 * about the account we're looking at" — the same not-yet-known reading
 * hasNoPaymentMethod gives an unloaded payload. The stamp travels ON the
 * payload rather than beside it, so the two can never drift apart by a render.
 */
export const SUBSCRIPTION_ACCOUNT_TOKEN = '__fetchedForToken';

/** Tag a getMySubscription response with the token the request carried. */
export const stampSubscriptionAccount = (response, token) => ({
  ...response,
  [SUBSCRIPTION_ACCOUNT_TOKEN]: token,
});

/**
 * True only when this payload is known to describe the account that's signed
 * in right now. Unstamped payloads (the getMySubscription error fallbacks, or
 * anything built by hand) read as false — never as "belongs to this user".
 */
export const isSubscriptionForCurrentAccount = (subscriptionDetails) => {
  const stamp = subscriptionDetails?.[SUBSCRIPTION_ACCOUNT_TOKEN];
  if (!stamp) return false;
  try {
    return stamp === localStorage.getItem('token');
  } catch (e) {
    return false;
  }
};

/**
 * ── Whose card is it anyway? ──
 *
 * A manager-managed account never holds a card of its own. The manager paid
 * with the MANAGER's card (see the `managerCard` flag through the create-user
 * flows), so getMySubscription reports a paid, active plan with
 * paymentMethods: [] — a perfectly healthy account that owes us nothing. Both
 * halves of the manager relationship look like this:
 *
 *   • the user a manager created and pays for, logging in herself
 *   • the manager / affiliate account itself, which pays per user at creation
 *     time instead of keeping a card on file
 *
 * /getUserAccess answers exactly that question — "is this account in a
 * manager-access relationship" — and its `success: true` is what kept the card
 * dialog away from these accounts before this module existed. It is the WRONG
 * test for "is a manager driving this session right now" (it stays true on the
 * user's own login, which is why the impersonation markers exist alongside it),
 * but it is the RIGHT test for "is someone else responsible for this bill".
 *
 * An answer that hasn't arrived yet — undefined, or the null the quick-user
 * exit sets — reads as NOT KNOWN, and therefore as "don't gate": the same
 * fail-open default the rest of this module takes. Without that, the gate would
 * fire in the gap before /getUserAccess returns and strand the account on the
 * payment page, since nothing navigates back out once it releases.
 */
export const ownsItsOwnBilling = (getAccessChecker) => !!getAccessChecker
  && getAccessChecker.success !== true;

// Plan ids that should never be gated, on top of the Lifetime rule. Empty by
// design — add '15'/'16' here if gift-plan recipients should also be let
// through without a card.
export const GATE_EXEMPT_PLAN_IDS = [];

// The backend's free plan. Matches the `subscription?.id !== '6'` test the
// "Unlock your Projector" dialog has always used to mean "on a paid plan".
const FREE_PLAN_IDS = ['6'];

/**
 * True when the account is on the free plan — a new signup that never bought
 * anything, or a quick user on the placeholder "Deliver & Stream" plan.
 *
 * There's nothing to bill a free plan for, so these accounts are never forced
 * to store a card. Checked the same belt-and-braces way as isLifetimePlan,
 * because no single field is reliably present:
 *   • the explicit is_free flag on the subscription
 *   • the plan NAME being "Free" — what app.jsx / sidebar / ListingModal test
 *   • the known free plan id
 *   • a $0 / is_free entry in allPlans
 */
export const isFreePlan = (subscriptionDetails, allPlans) => {
  const sub = subscriptionDetails?.subscription;
  if (!sub) return false;

  if (`${sub.is_free}` === '1' || sub.is_free === true) return true;

  const names = [subscriptionDetails?.current_plan, sub.plan, sub.product_name];
  if (names.some((n) => typeof n === 'string' && n.trim().toLowerCase() === 'free')) return true;

  const planId = `${sub.plan_id ?? sub.id ?? ''}`;
  if (FREE_PLAN_IDS.includes(planId)) return true;

  if (Array.isArray(allPlans)) {
    const matched = allPlans.find((p) => `${p?.id}` === planId);
    if (matched && (`${matched.is_free}` === '1' || parseFloat(matched.amount || 0) === 0)) return true;
  }

  return false;
};

/**
 * Same expired/suspended rule app.jsx applies to setInActiveUser. Those
 * accounts render a reduced route table with no /quick-user-payment entry, so
 * sending them there would bounce off the catch-all forever.
 */
export const isInactiveSubscription = (subscriptionDetails) => (
  (!subscriptionDetails?.has_activePlan
    && (subscriptionDetails?.expired || subscriptionDetails?.subscription !== null))
  || (subscriptionDetails?.has_activePlan
    && parseInt(subscriptionDetails?.subscription?.subscription_status, 10) === 2)
);

/**
 * THE decision, in one place: does this account have to add a card before it's
 * allowed to use the app? Takes a raw getMySubscription response, so it can be
 * called straight off the login request as well as off AppContext state.
 *
 * Only an account on a PAID, active plan with no card on file is held. Every
 * other case has nothing to bill, so forcing a card would be noise:
 *   • no card missing            → nothing to ask for
 *   • expired / suspended        → handled by its own flow
 *   • Lifetime                   → paid once, forever
 *   • free plan, or no plan yet  → new signups and quick users
 */
export const needsPaymentMethod = (subscriptionDetails, allPlans) => {
  if (!hasNoPaymentMethod(subscriptionDetails)) return false;
  if (isInactiveSubscription(subscriptionDetails)) return false;
  if (isLifetimePlan(subscriptionDetails, allPlans)) return false;
  // No subscription at all is treated like the free plan — there's no paid
  // plan behind the account, so there's nothing a card would be charged for.
  if (!subscriptionDetails?.subscription) return false;
  if (isFreePlan(subscriptionDetails, allPlans)) return false;
  const planId = subscriptionDetails?.subscription?.plan_id
    ?? subscriptionDetails?.subscription?.id;
  return !GATE_EXEMPT_PLAN_IDS.includes(`${planId}`);
};

/**
 * Onboarding / auth / public routes. The card dialogs stay hidden on these and
 * the no-card redirect leaves them alone, so a brand-new user can still walk
 * setpassword → avatar → interview intro → quick-user-payment, and a gift
 * recipient can still open their gift.
 *
 * Matched as path PREFIXES so parameterised routes (/share/:key, /interview/
 * join/:roomId, /plans/:type/:id …) are covered by their base.
 */
export const ONBOARDING_PATH_PREFIXES = [
  // Quick-user onboarding lane
  '/quick-user-payment',
  '/quick-user-welcome',
  '/quick-user-upload',
  '/setpassword',
  '/avatar',
  '/choose-occasion',
  '/welcome',
  '/interview',
  // Gift flow
  '/gift',
  '/gift2',
  '/gift-content',
  '/gift-payment',
  '/receiving-gift',
  // Contribute / mobile hand-offs and public share links
  '/contribute',
  '/contribute-mobile-upload',
  '/mobile-upload',
  '/mobile-camera',
  '/share/',
  '/shrt/',
  // Auth
  '/newSign-in',
  '/newSign-Up',
  '/sign-in',
  '/sign-up',
  '/forgot-password',
  '/verify-email',
  '/verify-your-email',
  '/verify-email-fallback',
  '/email-verification',
  '/mobile-verification',
  '/newMobile-verification',
  '/verify-code',
  '/newVerification-code',
  '/auto-verify',
  '/autoLogin',
  '/autoTVLogin',
  '/tv-login',
  '/tv-login-approve',
  '/link-device',
  // Static / legal
  '/privacy',
  '/terms-of-use',
  '/terms-and-services',
  '/subscriberAgreement',
];

/**
 * Routes that exist to TAKE a payment from someone OTHER than the gated user.
 *
 * Deliberately short. The lock is meant to be a lock: /plans, /switchplan and
 * /affiliate are NOT here, because a gated user could otherwise walk out of
 * /quick-user-payment just by typing one of them in the address bar. Adding a
 * card on the gate page unlocks the whole app, so this is not a dead end.
 *
 * (If the page's commented-out "Explore Other Plans" button is ever
 * re-enabled, '/switchplan' has to be added back here or that button will
 * bounce straight back to the gate.)
 */
export const PAYMENT_PATH_PREFIXES = [
  '/pay-by-manger',
];

const matchesPrefix = (pathname, prefixes) => prefixes.some(
  (p) => pathname === p || pathname.startsWith(p.endsWith('/') ? p : `${p}/`)
);

/**
 * The gift flow also travels on ordinary routes carrying ?gift=true /
 * ?fromGift=true, and hands off to /cloud-upload with a session marker. Same
 * two escapes the card dialogs already honour in app.jsx.
 */
const isGiftFlowContext = (pathname = '', search = '') => {
  const params = new URLSearchParams(search);
  if (params.get('gift') === 'true' || params.get('fromGift') === 'true') return true;
  if (pathname === '/cloud-upload') {
    try {
      return !!sessionStorage.getItem('giftFlowCloudConnect');
    } catch (e) {
      return false;
    }
  }
  return false;
};

/** Onboarding / auth / public page — the card dialogs must not appear here. */
export const isOnboardingPath = (pathname = '') => matchesPrefix(pathname, ONBOARDING_PATH_PREFIXES);

/** Page the no-card redirect must leave alone. */
export const isPaymentGateExemptPath = (pathname = '', search = '') => matchesPrefix(pathname, ONBOARDING_PATH_PREFIXES)
  || matchesPrefix(pathname, PAYMENT_PATH_PREFIXES)
  || isGiftFlowContext(pathname, search);
